import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material'
import Button from 'src/components/era-package/forms/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose, faSearch } from '@fortawesome/pro-regular-svg-icons'
import AbiSKUMgmtApiUrls from '../../../apiUrls/AbiSKUMgmtApiUrls'
import { FileForUserGetPartyId } from '../../../common/FileForUserCommon'

const SKUMgmtLookupDialog = ({
  open,
  onClose,
  onSelect,
  title = 'SKU Lookup',
  initialSearchText = ''
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchText)
  const [tradePartners, setTradePartners] = useState([])
  const [filteredSKUs, setFilteredSKUs] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedSKU, setSelectedSKU] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const dialogRef = useRef(null)

  // Fetch trade partners when dialog opens
  useEffect(() => {
    if (open) {
      fetchSKU()
      setSearchTerm(initialSearchText)
    }
  }, [open, initialSearchText])

  // Filter partners based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredSKUs(tradePartners)
    } else {
      const filtered = tradePartners.filter(partner => {
        const searchLower = searchTerm.toLowerCase()
        return (
          partner.skuReference?.toLowerCase().includes(searchLower) ||
          partner.skuDesc?.toLowerCase().includes(searchLower) ||
          partner.htsNumber?.toLowerCase().includes(searchLower) 
        )
      })
      setFilteredSKUs(filtered)
    }
    // Reset to first page when search changes
    setPage(0)
  }, [searchTerm, tradePartners])

  const fetchSKU = async () => {
    setLoading(true)
    try {
      const response = await fetch(
        AbiSKUMgmtApiUrls.GET + `?partyId=${FileForUserGetPartyId()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          }
        }
      )

      if (response.ok) {
        const json = await response.json()
        const partners = json?.data || []
        setTradePartners(partners)
        setFilteredSKUs(partners)
      } else {
        console.error('Error fetching sku')
        setTradePartners([])
        setFilteredSKUs([])
      }
    } catch (error) {
      console.error('Error fetching sku:', error)
      setTradePartners([])
      setFilteredSKUs([])
    } finally {
      setLoading(false)
    }
  }

  const handleRowClick = (partner) => {
    setSelectedSKU(partner)
  }

  const handleSelect = () => {
    if (selectedSKU && typeof onSelect === 'function') {
      onSelect(selectedSKU)
    }
    handleClose()
  }

  const handleClose = () => {
    setSearchTerm('')
    setSelectedSKU(null)
    setPage(0)
    if (typeof onClose === 'function') {
      onClose()
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  // Get current page data
  const paginatedPartners = filteredSKUs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      ref={dialogRef}
      onKeyDown={handleKeyDown}
      aria-labelledby="trade-mgmt-lookup-title"
    >
      <DialogTitle
        id="sku-mgmt-lookup-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {title}
        <IconButton
          aria-label="Close lookup dialog"
          onClick={handleClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <FontAwesomeIcon icon={faClose} width="1em" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search by SKU Reference, Description, HTC..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faSearch} />
              </InputAdornment>
            )
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} />
                </InputAdornment>
              )
            }
          }}
        />

        {/* Results Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>SKU Reference</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>HTS</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>C/O</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>PGA</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSKUs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {searchTerm ? 'No matching sku found' : 'No sku available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPartners.map((partner) => (
                    <TableRow
                      key={partner.id}
                      hover
                      selected={selectedSKU?.id === partner.id}
                      onClick={() => handleRowClick(partner)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{partner.skuReference || '-'}</TableCell>
                      <TableCell>{partner.skuDesc || '-'}</TableCell>
                      <TableCell>{partner.htsNumber || '-'}</TableCell>
                      <TableCell>{partner.countryOfOrigin || '-'}</TableCell>
                      <TableCell>{partner.pgaList || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {filteredSKUs.length > 0 && (
          <TablePagination
            component="div"
            count={filteredSKUs.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ pt: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSelect}
          disabled={!selectedSKU}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SKUMgmtLookupDialog
